import { 
  collection, 
  getDocs, 
  getDoc, 
  doc, 
  addDoc, 
  updateDoc, 
  serverTimestamp, 
  query, 
  orderBy, 
  limit,
  setDoc,
  deleteDoc,
  where
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from './firebase';

// Import local storage fallback
import {
  saveCommentLocally,
  saveReplyLocally,
  getLocalComments,
  getLocalReplies,
  clearAllLocalComments,
  deleteLocalComment,
  deleteLocalReply
} from './localCommentStore';

// Collection references
const questionsRef = collection(db, 'questions');
const commentsRef = collection(db, 'comments');

// Get all questions with pagination
export const getAllQuestions = async (page = 1, limit = 10, userId = null, personalized = false, hotMode = null) => {
  // NUCLEAR OPTION: Force anonymous behavior AND increase limit
  const originalUserId = userId;
  const originalPersonalized = personalized;
  const originalLimit = limit;
  
  userId = 'anonymous_user'; // Force anonymous behavior  
  personalized = false; // Disable personalization
  limit = 50; // Force higher limit to show more questions
  
  const timestamp = new Date().toISOString();
  console.log(`🚨🚨🚨 CACHE BUST v4: NEW CODE LOADED AT ${timestamp} 🚨🚨🚨`);
  console.log(`🔧 FORCED ANONYMOUS: userId was ${originalUserId}, now ${userId}`);
  console.log(`🔧 PERSONALIZATION DISABLED: was ${originalPersonalized}, now ${personalized}`);  
  console.log(`🔧 LIMIT INCREASED: was ${originalLimit}, now ${limit}`);
  console.error('FORCING CONSOLE ERROR TO MAKE IT VISIBLE:', { timestamp, originalUserId, userId, originalPersonalized, personalized, originalLimit, limit });
  try {
    console.log('===== ADVANCED ROTATION + MODIFIED ALGORITHM ACTIVE =====');
    console.log('Using personalized content rotation per page view');
    console.log('Fetching all questions...', personalized ? '(personalized)' : '', hotMode ? `(hot mode: ${hotMode})` : '');

    // Get all question documents without filtering
    const allDocsSnapshot = await getDocs(questionsRef);

    if (allDocsSnapshot.empty) {
      console.log('No questions found in database');
      // Auto-create some sample questions if none exist
      await createSampleQuestionsIfEmpty();
      return { questions: [], hasMore: false };
    }

    console.log(`Found ${allDocsSnapshot.size} total question documents`);
    console.log('===== DEBUGGING: Question fetching =====');
    
    // If we have very few questions (less than 5), create more sample questions
    if (allDocsSnapshot.size < 5) {
      console.log('Very few questions detected, creating additional sample questions...');
      createSampleQuestionsIfEmpty().catch(err => console.error('Error creating sample questions:', err));
    }

    let allQuestions = [];

    // Process all questions
    allDocsSnapshot.forEach(doc => {
      const data = doc.data();
      const questionData = {
        id: doc.id,
        ...data,
        // Ensure createdAt exists as an ISO string
        createdAt: data.createdAt ?
                   (typeof data.createdAt === 'string' ? data.createdAt : new Date().toISOString())
                   : new Date().toISOString()
      };

      // Handle missing fields
      if (!questionData.optionAVotes) questionData.optionAVotes = 0;
      if (!questionData.optionBVotes) questionData.optionBVotes = 0;
      if (!questionData.totalVotes) questionData.totalVotes = 0;

      // Make sure styles exist
      if (!questionData.styles) {
        questionData.styles = {
          optionABackgroundColor: 'white',
          optionATextColor: 'black',
          optionBBackgroundColor: 'black',
          optionBTextColor: 'white',
          resultBarColor: 'white'
        };
      }

      // Add all questions to array (including first_question)
      allQuestions.push(questionData);
    });

    // DEBUG INFO: Log total questions before filtering
    console.log(`Total questions before processing: ${allQuestions.length}`);
    console.log('Questions IDs:', allQuestions.map(q => q.id));
    console.log('Questions texts:', allQuestions.map(q => q.questionText));

    // FORCED ROTATION: Get page load count to use a different one each time
    let loadCount = 0;
    try {
      loadCount = parseInt(localStorage.getItem('forceRotationCounter') || '0');
      loadCount++;
      localStorage.setItem('forceRotationCounter', loadCount.toString());
      console.log(`⚠️ FORCE ROTATION COUNTER: ${loadCount}`);
    } catch (err) {
      console.warn('Error managing rotation counter', err);
    }

    // 🔥 ALGORITHM WIPED: Just show all questions in random order, no filtering whatsoever
    console.log(`🔥 ALGORITHM WIPED - RAW MODE: Showing ALL ${allQuestions.length} questions with simple shuffle`);
    
    // Simple shuffle of ALL questions - no personalization, no filtering, no complexity
    for (let i = allQuestions.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [allQuestions[i], allQuestions[j]] = [allQuestions[j], allQuestions[i]];
    }
    
    console.log(`✅ Shuffled ${allQuestions.length} questions - should see all of them now`);

    // Calculate pagination
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    const paginatedQuestions = allQuestions.slice(startIndex, endIndex);

    // Final debug info
    console.log(`Returning ${paginatedQuestions.length} questions for page ${page}`);
    console.log(`Question IDs in order: ${paginatedQuestions.map(q => q.id).join(', ')}`);
    console.log(`First question in feed: ${paginatedQuestions[0]?.questionText || 'None'}`);
    console.log(`HasMore: ${allQuestions.length > endIndex}, Total: ${allQuestions.length}, EndIndex: ${endIndex}`);
    console.log('===== END OF EXTREME DEBUG =====');

    // NUCLEAR OVERRIDE: If we only have 2 or fewer questions in the result, return ALL questions
    if (paginatedQuestions.length <= 2 && allQuestions.length > 2) {
      console.log('🚨 OVERRIDE: Only 2 questions returned, forcing ALL questions to show');
      console.log('🚨 OVERRIDE: Ignoring pagination and returning all questions');
      
      // ROBUST shuffle with forced unique questions + debug info
      const shuffledAll = [...allQuestions];
      
      // Remove any potential duplicate questions by ID
      const uniqueQuestions = shuffledAll.filter((question, index, self) => 
        index === self.findIndex(q => q.id === question.id)
      );
      
      console.log(`🔍 NUCLEAR DEBUG: Original ${shuffledAll.length}, Unique ${uniqueQuestions.length}`);
      console.log('🔍 Question IDs:', uniqueQuestions.map(q => `${q.id}: "${q.questionText.slice(0, 30)}..."`));
      
      // Fisher-Yates shuffle with extra randomness
      for (let i = uniqueQuestions.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [uniqueQuestions[i], uniqueQuestions[j]] = [uniqueQuestions[j], uniqueQuestions[i]];
      }
      
      return {
        questions: uniqueQuestions, // Return unique shuffled questions
        hasMore: false, // No more needed since we're showing all
        page: 1,
        totalCount: shuffledAll.length
      };
    }

    return {
      questions: paginatedQuestions,
      hasMore: allQuestions.length > endIndex,
      page,
      totalCount: allQuestions.length
    };
  } catch (error) {
    console.error('Error getting questions:', error);
    throw error;
  }
};

// Function to apply different hot mode filters
const applyHotModeFilter = (questions, hotMode, userId) => {
  console.log(`Applying "${hotMode}" algorithm to ${questions.length} questions`);

  // Prepare additional data needed for different algorithms
  let processedQuestions = questions.map(question => {
    // Calculate vote velocity (votes per day since creation)
    const creationDate = new Date(question.createdAt);
    const ageInDays = Math.max(0.1, (new Date() - creationDate) / (1000 * 60 * 60 * 24));
    const voteVelocity = question.totalVotes / ageInDays;

    // Calculate engagement rate (votes and comments)
    // First, try to get comment count from localStorage to avoid Firebase calls
    let commentCount = 0;
    try {
      const localComments = JSON.parse(localStorage.getItem('comments') || '{}');
      commentCount = localComments[question.id]?.length || 0;
    } catch (err) {
      console.warn('Error getting comment count from localStorage:', err);
    }

    // Calculate controversy score (how close the vote split is to 50/50)
    const voteSplit = question.totalVotes > 0
      ? Math.min(question.optionAVotes, question.optionBVotes) / question.totalVotes
      : 0;
    const controversyScore = voteSplit * 2; // 0 means all votes on one side, 1 means perfect 50/50 split

    // Calculate recency score (newer = higher score, exponentially decaying)
    const daysSinceCreation = ageInDays;
    const recencyScore = Math.exp(-daysSinceCreation / 7); // exponential decay, half-life of 7 days

    return {
      ...question,
      _metrics: {
        voteVelocity,
        commentCount,
        controversyScore,
        recencyScore,
        ageInDays
      }
    };
  });

  // Apply different algorithms based on hot mode
  switch(hotMode) {
    case 'trending':
      // Trending prioritizes recent activity with high velocity
      processedQuestions.sort((a, b) => {
        // Trending formula: velocity * recency with strong recency bias
        const scoreA = a._metrics.voteVelocity * Math.pow(a._metrics.recencyScore, 1.5);
        const scoreB = b._metrics.voteVelocity * Math.pow(b._metrics.recencyScore, 1.5);
        return scoreB - scoreA; // Higher score first
      });
      break;

    case 'popular':
      // Popular prioritizes questions with highest total engagement regardless of time
      processedQuestions.sort((a, b) => {
        // Popular formula: total votes + comments
        const scoreA = a.totalVotes + a._metrics.commentCount;
        const scoreB = b.totalVotes + b._metrics.commentCount;
        return scoreB - scoreA; // Higher score first
      });
      break;

    case 'rising':
      // Rising prioritizes questions showing sudden spikes in engagement
      processedQuestions.sort((a, b) => {
        // Rising formula: velocity with extra weight for very recent questions
        // and penalties for older content
        const ageFactorA = Math.pow(0.8, a._metrics.ageInDays); // exponential decay
        const ageFactorB = Math.pow(0.8, b._metrics.ageInDays);
        const scoreA = a._metrics.voteVelocity * ageFactorA;
        const scoreB = b._metrics.voteVelocity * ageFactorB;
        return scoreB - scoreA; // Higher score first
      });
      break;

    case 'new':
      // New prioritizes newest questions first
      processedQuestions.sort((a, b) => {
        const dateA = new Date(a.createdAt);
        const dateB = new Date(b.createdAt);
        return dateB - dateA; // Latest first
      });
      break;

    case 'viral':
      // Viral prioritizes questions with extreme voting patterns or rapid growth
      processedQuestions.sort((a, b) => {
        // Viral formula: high velocity questions that aren't controversial
        // We actually want viral content to be polarizing (not 50/50 splits)
        const viralityA = a._metrics.voteVelocity * (1 - a._metrics.controversyScore) * a._metrics.recencyScore;
        const viralityB = b._metrics.voteVelocity * (1 - b._metrics.controversyScore) * b._metrics.recencyScore;
        return viralityB - viralityA; // Higher score first
      });
      break;

    case 'best':
      // Best prioritizes highest quality content based on vote balance and engagement
      processedQuestions.sort((a, b) => {
        // Best formula: combination of popularity and controversy (we want some controversy for interest)
        // This also considers user engagement if userId is available
        const controversyMultiplierA = 0.5 + (a._metrics.controversyScore * 0.5); // value between 0.5-1
        const controversyMultiplierB = 0.5 + (b._metrics.controversyScore * 0.5);

        // Calculate base scores
        const scoreA = (a.totalVotes + a._metrics.commentCount) * controversyMultiplierA;
        const scoreB = (b.totalVotes + b._metrics.commentCount) * controversyMultiplierB;

        return scoreB - scoreA; // Higher score first
      });
      break;

    default:
      // If no valid hot mode, just return in default order
      console.warn(`Unknown hot mode: ${hotMode}, using default sorting`);
      break;
  }

  // Remove internal metrics before returning
  return processedQuestions.map(q => {
    const { _metrics, ...cleanQuestion } = q;
    return cleanQuestion;
  });
};

// Enhanced personalize questions for a specific user - integrates hot algorithms
const personalizeQuestions = (questions, userId) => {
  console.log("==== RUNNING ENHANCED PERSONALIZATION WITH HOT INTEGRATION ====");

  // Force different results on each page load with a counter
  let pageLoadCounter = 0;
  try {
    pageLoadCounter = parseInt(localStorage.getItem('forYouPageLoadCounter') || '0');
    pageLoadCounter++;
    localStorage.setItem('forYouPageLoadCounter', pageLoadCounter.toString());
    console.log("PAGE LOAD COUNTER INCREMENTED TO:", pageLoadCounter);
  } catch (err) {
    console.warn("Error with page load counter:", err);
  }

  if (!userId || !questions || questions.length === 0) {
    console.log("No user ID or questions to personalize - returning unmodified");
    return questions;
  }

  console.log(`Personalizing questions for user ${userId}`);

  try {
    // ===== DATA GATHERING =====

    // 1. Load user's interests (starred categories)
    const userInterests = JSON.parse(localStorage.getItem('userInterests') || '{}');

    // 2. Load voted questions to filter them out
    let votedQuestions = {};

    // Try to load user-specific voted questions
    if (userId) {
      const userVotedQuestionsKey = `votedQuestions_${userId}`;
      votedQuestions = JSON.parse(localStorage.getItem(userVotedQuestionsKey) || '{}');
    } else {
      // Anonymous user
      const anonVotedQuestionsKey = `votedQuestions_anonymous`;
      votedQuestions = JSON.parse(localStorage.getItem(anonVotedQuestionsKey) || '{}');
    }

    console.log(`User has voted on ${Object.keys(votedQuestions).length} questions`);
    console.log(`User has ${Object.keys(userInterests).filter(k => userInterests[k]).length} starred categories`);

    // 3. Get categories from user's voting history (implicit interests)
    const votingHistory = getVotingHistoryCategories(votedQuestions, questions);
    console.log(`User has voted on questions from ${Object.keys(votingHistory).length} categories`);

    // 4. Track user's vote choices to understand preferences
    const userVoteOptions = {};
    if (userId) {
      const userVoteOptionsKey = `userVoteOptions_${userId}`;
      userVoteOptions = JSON.parse(localStorage.getItem(userVoteOptionsKey) || '{}');
    } else {
      const anonVoteOptionsKey = `userVoteOptions_anonymous`;
      userVoteOptions = JSON.parse(localStorage.getItem(anonVoteOptionsKey) || '{}');
    }

    // Count votes for each option to detect preference pattern
    const optionACount = Object.values(userVoteOptions).filter(option => option === 'A').length;
    const optionBCount = Object.values(userVoteOptions).filter(option => option === 'B').length;
    const hasOptionPreference = Math.abs(optionACount - optionBCount) > 5 && (optionACount + optionBCount) > 10;
    const preferredOption = optionACount > optionBCount ? 'A' : 'B';

    console.log(`User vote pattern: A:${optionACount}, B:${optionBCount}`);

    // 5. Track engagement through comments
    let userCommentedQuestions = new Set();
    let commentCountByCategory = {};

    try {
      // Try to get all comments from localStorage
      const localComments = JSON.parse(localStorage.getItem('comments') || '{}');

      // Look for questions where this user has commented
      Object.keys(localComments).forEach(questionId => {
        const comments = localComments[questionId] || [];
        const userComments = comments.filter(comment => comment.userId === userId);

        if (userComments.length > 0) {
          userCommentedQuestions.add(questionId);

          // Find the category for this question
          const questionData = questions.find(q => q.id === questionId);
          if (questionData && questionData.category) {
            commentCountByCategory[questionData.category] =
              (commentCountByCategory[questionData.category] || 0) + userComments.length;
          }
        }
      });

      console.log(`User has commented on ${userCommentedQuestions.size} questions`);
    } catch (err) {
      console.warn('Error getting user comment history:', err);
    }

    // 6. Calculate time spent on questions (if available)
    const questionTimeSpent = {};
    try {
      const timeSpentKey = `questionTimeSpent_${userId}`;
      const savedTimeData = JSON.parse(localStorage.getItem(timeSpentKey) || '{}');

      // Normalize time spent - anything over 60 seconds counts as "high engagement"
      Object.keys(savedTimeData).forEach(qId => {
        questionTimeSpent[qId] = Math.min(savedTimeData[qId] / 60, 1);
      });
    } catch (err) {
      console.warn('Error getting time spent data:', err);
    }

    // 7. Get user skipped questions (if tracking implemented)
    const skippedQuestions = new Set();
    try {
      const skippedKey = `skippedQuestions_${userId}`;
      const skippedData = JSON.parse(localStorage.getItem(skippedKey) || '[]');
      skippedData.forEach(qId => skippedQuestions.add(qId));
    } catch (err) {
      console.warn('Error getting skipped questions:', err);
    }

    // 8. Get user time-of-day preferences
    const currentHour = new Date().getHours();
    const timeOfDay =
      currentHour >= 5 && currentHour < 12 ? 'morning' :
      currentHour >= 12 && currentHour < 17 ? 'afternoon' :
      currentHour >= 17 && currentHour < 22 ? 'evening' : 'night';

    // Try to get time-of-day category preferences
    const timePreferences = {};
    try {
      const timePrefsKey = `timePreferences_${userId}`;
      const savedTimePrefs = JSON.parse(localStorage.getItem(timePrefsKey) || '{}');
      Object.assign(timePreferences, savedTimePrefs);
    } catch (err) {
      console.warn('Error getting time preferences:', err);
    }

    // 9. Get user hot section preferences (which hot tabs they click most)
    const hotTabPreferences = {};
    try {
      const hotTabPrefsKey = `hotTabPreferences_${userId}`;
      const savedHotTabPrefs = JSON.parse(localStorage.getItem(hotTabPrefsKey) || '{}');
      Object.assign(hotTabPreferences, savedHotTabPrefs);
    } catch (err) {
      console.warn('Error getting hot tab preferences:', err);
    }

    // ===== CONTENT SELECTION =====

    // 10. Weighted prioritization: Show all questions but prioritize unvoted ones
    const unvotedCount = questions.filter(q => !votedQuestions[q.id]).length;
    console.log(`${unvotedCount} unvoted questions available out of ${questions.length} total`);
    
    // Add priority weights to all questions
    let weightedQuestions = questions.map(question => ({
      ...question,
      priorityWeight: votedQuestions[question.id] ? 0.1 : 1.0 // 10x higher priority for unvoted
    }));
    
    const cacheTimestamp = new Date().toISOString();
    console.log(`🚨🚨🚨 WEIGHTED PRIORITIZATION ACTIVE - CACHE BUST v${cacheTimestamp} 🚨🚨🚨`);
    console.log('✨ Using weighted prioritization: unvoted questions get 10x higher priority');

    // ===== PRE-PROCESSING WITH HOT ALGORITHMS =====

    // Calculate metrics for all questions (same as in applyHotModeFilter)
    let processedQuestions = weightedQuestions.map(question => {
      // Calculate vote velocity (votes per day since creation)
      const creationDate = new Date(question.createdAt);
      const ageInDays = Math.max(0.1, (new Date() - creationDate) / (1000 * 60 * 60 * 24));
      const voteVelocity = question.totalVotes / ageInDays;

      // Calculate engagement rate (votes and comments)
      // First, try to get comment count from localStorage to avoid Firebase calls
      let commentCount = 0;
      try {
        const localComments = JSON.parse(localStorage.getItem('comments') || '{}');
        commentCount = localComments[question.id]?.length || 0;
      } catch (err) {
        console.warn('Error getting comment count from localStorage:', err);
      }

      // Calculate controversy score (how close the vote split is to 50/50)
      const voteSplit = question.totalVotes > 0
        ? Math.min(question.optionAVotes, question.optionBVotes) / question.totalVotes
        : 0;
      const controversyScore = voteSplit * 2; // 0 means all votes on one side, 1 means perfect 50/50 split

      // Calculate recency score (newer = higher score, exponentially decaying)
      const daysSinceCreation = ageInDays;
      const recencyScore = Math.exp(-daysSinceCreation / 7); // exponential decay, half-life of 7 days

      return {
        ...question,
        _metrics: {
          voteVelocity,
          commentCount,
          controversyScore,
          recencyScore,
          ageInDays
        }
      };
    });

    // 11. Get trending questions based on vote velocity
    const questionVoteVelocity = {};

    // Calculate vote velocity (total votes / age in days)
    processedQuestions.forEach(q => {
      questionVoteVelocity[q.id] = q._metrics.voteVelocity;
    });

    // 12. Ensure diversity by tracking categories we've already selected
    const includedCategories = new Set();
    const maxQuestionsPerCategory = Math.ceil(weightedQuestions.length * 0.3); // Max 30% from one category
    const categoryQuestionCount = {};

    // No longer need fallback logic - using weighted prioritization for all questions

    // ===== CREATE HOT CONTENT SEGMENTS =====
    // Prepare special segments from each hot algorithm to enhance personalization

    // Make a copy so we don't modify the original
    let workingQuestions = [...processedQuestions];

    // Create a set of hot segments (take top questions from each hot algorithm)
    const hotSegments = {
      trending: [],
      popular: [],
      rising: [],
      viral: [],
      best: []
    };

    // Number of questions to take from each hot segment
    const questionsPerHotSegment = Math.min(3, Math.ceil(workingQuestions.length / 10));

    if (workingQuestions.length > 0) {
      // Sort by trending algorithm
      const trendingQuestions = [...workingQuestions].sort((a, b) => {
        const scoreA = a._metrics.voteVelocity * Math.pow(a._metrics.recencyScore, 1.5);
        const scoreB = b._metrics.voteVelocity * Math.pow(b._metrics.recencyScore, 1.5);
        return scoreB - scoreA;
      }).slice(0, questionsPerHotSegment);

      // Store trending segment
      hotSegments.trending = trendingQuestions;

      // Sort by popular algorithm
      const popularQuestions = [...workingQuestions].sort((a, b) => {
        const scoreA = a.totalVotes + a._metrics.commentCount;
        const scoreB = b.totalVotes + b._metrics.commentCount;
        return scoreB - scoreA;
      }).slice(0, questionsPerHotSegment);

      // Store popular segment
      hotSegments.popular = popularQuestions;

      // Sort by rising algorithm
      const risingQuestions = [...workingQuestions].sort((a, b) => {
        const ageFactorA = Math.pow(0.8, a._metrics.ageInDays);
        const ageFactorB = Math.pow(0.8, b._metrics.ageInDays);
        const scoreA = a._metrics.voteVelocity * ageFactorA;
        const scoreB = b._metrics.voteVelocity * ageFactorB;
        return scoreB - scoreA;
      }).slice(0, questionsPerHotSegment);

      // Store rising segment
      hotSegments.rising = risingQuestions;

      // Sort by viral algorithm
      const viralQuestions = [...workingQuestions].sort((a, b) => {
        const viralityA = a._metrics.voteVelocity * (1 - a._metrics.controversyScore) * a._metrics.recencyScore;
        const viralityB = b._metrics.voteVelocity * (1 - b._metrics.controversyScore) * b._metrics.recencyScore;
        return viralityB - viralityA;
      }).slice(0, questionsPerHotSegment);

      // Store viral segment
      hotSegments.viral = viralQuestions;

      // Sort by best algorithm
      const bestQuestions = [...workingQuestions].sort((a, b) => {
        const controversyMultiplierA = 0.5 + (a._metrics.controversyScore * 0.5);
        const controversyMultiplierB = 0.5 + (b._metrics.controversyScore * 0.5);
        const scoreA = (a.totalVotes + a._metrics.commentCount) * controversyMultiplierA;
        const scoreB = (b.totalVotes + b._metrics.commentCount) * controversyMultiplierB;
        return scoreB - scoreA;
      }).slice(0, questionsPerHotSegment);

      // Store best segment
      hotSegments.best = bestQuestions;
    }

    // ===== SCORING FACTORS =====

    // 13. Calculate interest scores for each question with improved weights and hot algorithms
    const scoredQuestions = processedQuestions.map(question => {
      const categoryId = question.category || '';
      categoryQuestionCount[categoryId] = (categoryQuestionCount[categoryId] || 0) + 1;
      includedCategories.add(categoryId);

      // Initialize score buckets for better debugging
      const scoreFactors = {
        base: 0,
        explicit: 0,
        implicit: 0,
        engagement: 0,
        diversity: 0,
        recency: 0,
        trending: 0,
        timeOfDay: 0,
        hotAlgorithm: 0,
        random: 0
      };

      // ===== INTEREST-BASED FACTORS (35% of total possible score) =====

      // Base randomness (to avoid completely deterministic results)
      scoreFactors.base = 0.05;

      // Strong boost for starred categories (explicit interests)
      if (categoryId && userInterests[categoryId]) {
        scoreFactors.explicit = 0.20; // 20% boost for starred categories
      }

      // Medium boost for categories the user has voted on before (implicit interests)
      if (categoryId && votingHistory[categoryId]) {
        const categoryVoteCount = votingHistory[categoryId];
        // Scale by frequency but with diminishing returns
        scoreFactors.implicit = 0.10 * Math.min(categoryVoteCount / 3, 1); // Up to 10% boost

        // Apply time decay - more recent votes have more weight
        const recentVotes = Object.entries(votedQuestions)
          .filter(([qId, _]) => {
            const q = questions.find(question => question.id === qId);
            return q && q.category === categoryId;
          })
          .filter(([_, option]) => {
            const timestamp = option.timestamp;
            if (!timestamp) return false;
            const daysAgo = (Date.now() - timestamp) / (1000 * 60 * 60 * 24);
            return daysAgo < 14; // Votes in last 2 weeks
          });

        if (recentVotes.length > 0) {
          scoreFactors.implicit += 0.05; // Additional 5% for recent activity
        }
      }

      // ===== ENGAGEMENT FACTORS (20% of total possible score) =====

      // Option preference matching (if user has a clear A/B preference pattern)
      if (hasOptionPreference) {
        const questionHasPreferredOption = preferredOption === 'A' ?
          question.optionAVotes > question.optionBVotes :
          question.optionBVotes > question.optionAVotes;

        if (questionHasPreferredOption) {
          scoreFactors.engagement += 0.05; // 5% boost for questions that match user's general preference
        }
      }

      // Commented content bonus (boost the category if user has commented in it)
      if (categoryId && commentCountByCategory[categoryId]) {
        const commentScore = Math.min(commentCountByCategory[categoryId] / 5, 1) * 0.05;
        scoreFactors.engagement += commentScore; // Up to 5% boost based on comment activity
      }

      // Vote balance - boost questions with closer vote splits for more engaging content
      const voteRatio = question.totalVotes > 0 ?
        Math.min(question.optionAVotes, question.optionBVotes) / question.totalVotes : 0;
      scoreFactors.engagement += 0.05 * voteRatio; // Up to 5% boost for balanced questions

      // Time spent boost - if user spent more time on similar questions
      if (categoryId) {
        // Find questions in same category user spent time on
        const categoryTimeSpent = Object.entries(questionTimeSpent)
          .filter(([qId, _]) => {
            const q = questions.find(question => question.id === qId);
            return q && q.category === categoryId;
          })
          .reduce((sum, [_, time]) => sum + time, 0);

        if (categoryTimeSpent > 0) {
          scoreFactors.engagement += Math.min(categoryTimeSpent / 3, 0.05); // Up to 5% time spent boost
        }
      }

      // Penalize previously skipped questions slightly
      if (skippedQuestions.has(question.id)) {
        scoreFactors.engagement -= 0.10; // 10% penalty for previously skipped questions
      }

      // ===== DIVERSITY FACTORS (10% of total possible score) =====

      // Category diversity - slightly penalize overrepresented categories
      if (categoryId && categoryQuestionCount[categoryId] > maxQuestionsPerCategory) {
        scoreFactors.diversity -= 0.05; // 5% penalty for categories with too many questions
      } else {
        // Boost underrepresented categories
        scoreFactors.diversity += 0.05;
      }

      // Content fatigue factor - reduce score for recently seen categories
      try {
        const recentlySeenKey = `recentlySeenCategories_${userId}`;
        const recentlySeen = JSON.parse(localStorage.getItem(recentlySeenKey) || '{}');

        if (categoryId && recentlySeen[categoryId]) {
          const timeSinceLastSeen = (Date.now() - recentlySeen[categoryId]) / (1000 * 60 * 60);

          if (timeSinceLastSeen < 1) { // Seen in last hour
            scoreFactors.diversity -= 0.05; // 5% penalty for recently seen categories
          }
        } else {
          // Boost for fresh categories
          scoreFactors.diversity += 0.05; // 5% boost for categories not recently seen
        }
      } catch (err) {
        console.warn('Error applying content fatigue factor:', err);
      }

      // ===== TEMPORAL FACTORS (15% of total possible score) =====

      // Recency boost for newer questions (time decay function)
      if (question.createdAt) {
        const ageInDays = (new Date() - new Date(question.createdAt)) / (1000 * 60 * 60 * 24);
        // Stronger boost for newer content with smoother decay
        if (ageInDays < 14) {
          scoreFactors.recency = 0.10 * Math.exp(-ageInDays / 7); // Exponential decay over 2 weeks
        }
      }

      // Trending boost based on vote velocity
      const velocity = questionVoteVelocity[question.id] || 0;
      if (velocity > 0) {
        // Normalize the velocity across all questions
        const maxVelocity = Math.max(...Object.values(questionVoteVelocity).filter(v => !isNaN(v) && isFinite(v)));
        if (maxVelocity > 0) {
          const normalizedVelocity = velocity / maxVelocity;
          scoreFactors.trending = 0.05 * normalizedVelocity; // Up to 5% boost for trending content
        }
      }

      // Time of day preferences
      if (categoryId && timePreferences[timeOfDay] &&
          timePreferences[timeOfDay].includes(categoryId)) {
        scoreFactors.timeOfDay = 0.05; // 5% boost for categories preferred at this time of day
      }

      // ===== HOT ALGORITHM INTEGRATION (15% of total possible score) =====

      // Check if question is in any of the hot segments and boost accordingly
      let hotAlgorithmScore = 0;
      let algorithmMatches = 0;

      // Check each hot segment
      Object.entries(hotSegments).forEach(([algorithm, questions]) => {
        if (questions.some(q => q.id === question.id)) {
          algorithmMatches++;

          // Give extra weight to algorithms the user has shown interest in
          const algorithmInterestMultiplier = hotTabPreferences[algorithm] ?
            1 + (Math.min(hotTabPreferences[algorithm], 10) / 20) : 1; // 1.0 to 1.5x

          hotAlgorithmScore += 0.05 * algorithmInterestMultiplier;
        }
      });

      // Apply diminishing returns if in multiple segments
      if (algorithmMatches > 1) {
        hotAlgorithmScore = Math.min(0.15, hotAlgorithmScore); // Cap at 15%
      }

      scoreFactors.hotAlgorithm = hotAlgorithmScore;

      // ===== RANDOMIZATION (additional 0-5% random factor) =====

      // Small additional randomness to prevent identical scores and ensure variety
      scoreFactors.random = Math.random() * 0.05;

      // Calculate final score by summing all factors and applying priority weight
      const baseScore = Object.values(scoreFactors).reduce((sum, score) => sum + score, 0);
      const totalScore = baseScore * (question.priorityWeight || 1.0); // Apply 10x multiplier for unvoted questions

      // Track scoring details for debugging
      return {
        ...question,
        _personalizationScore: totalScore,
        _scoreFactors: scoreFactors,
        _priorityWeight: question.priorityWeight,
        _baseScore: baseScore
      };
    });

    // ===== RESULT PREPARATION =====

    // 14. Sort by score (highest first)
    scoredQuestions.sort((a, b) => b._personalizationScore - a._personalizationScore);

    // 15. INTEGRATION: CREATE A BLENDED FEED OF PERSONALIZED + HOT CONTENT

    // Get milliseconds component from current time for extra randomness
    const milliseconds = new Date().getMilliseconds();

    // Store previously shown first question in localStorage
    let previouslyShownFirst = null;
    try {
      previouslyShownFirst = localStorage.getItem('previouslyShownFirstQuestion');
      console.log("Previous first question:", previouslyShownFirst);
    } catch (err) {
      console.warn('Error getting previously shown questions from storage:', err);
    }

    // Create initial segments
    let finalBlendedQuestions = [];

    // A. FEATURED SEGMENT - Get top personalized question, but avoid repeating
    let featuredSegment = scoredQuestions.slice(0, 1);

    // Check if it would repeat the previous first question
    if (featuredSegment.length > 0 && featuredSegment[0].id === previouslyShownFirst) {
      // Take the next best question instead
      featuredSegment = [scoredQuestions[1] || scoredQuestions[0]];
    }

    // Save the new first question ID for next time
    if (featuredSegment.length > 0) {
      try {
        localStorage.setItem('previouslyShownFirstQuestion', featuredSegment[0].id);
        console.log("New first question set:", featuredSegment[0].id);
      } catch (err) {
        console.warn('Error storing first question in storage:', err);
      }
    }

    // B. PERSONALIZED SEGMENT - Get highly personalized content
    const personalizedSegment = scoredQuestions.slice(1, 5).filter(q =>
      !featuredSegment.some(f => f.id === q.id)
    );

    // C. HOT CONTENT SEGMENTS - Add variety from hot segments

    // Create a set to track questions we've already included
    const includedQuestionIds = new Set([
      ...featuredSegment.map(q => q.id),
      ...personalizedSegment.map(q => q.id)
    ]);

    // Randomly select hot modes to prioritize
    const hotModes = ['trending', 'popular', 'rising', 'viral', 'best'];

    // Shuffle hot modes for variety
    for (let i = hotModes.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [hotModes[i], hotModes[j]] = [hotModes[j], hotModes[i]];
    }

    // Prioritize hot modes the user has shown interest in
    hotModes.sort((a, b) => {
      const aInterest = hotTabPreferences[a] || 0;
      const bInterest = hotTabPreferences[b] || 0;
      return bInterest - aInterest;
    });

    // Get questions from hot segments that aren't already included
    const hotSegmentQuestions = [];

    // Take 1-2 questions from each hot segment
    hotModes.forEach(mode => {
      const modeQuestions = hotSegments[mode].filter(q => !includedQuestionIds.has(q.id));

      if (modeQuestions.length > 0) {
        // Take up to 2 questions from this mode
        const count = Math.min(2, modeQuestions.length);
        const selectedQuestions = modeQuestions.slice(0, count);

        // Add to our hot segment and track IDs
        hotSegmentQuestions.push(...selectedQuestions);
        selectedQuestions.forEach(q => includedQuestionIds.add(q.id));
      }
    });

    // D. DISCOVERY SEGMENT - Add completely random selection for discovery
    const remainingQuestions = scoredQuestions.filter(q => !includedQuestionIds.has(q.id));

    // Shuffle the remaining questions
    const shuffledRemaining = [...remainingQuestions];
    for (let i = shuffledRemaining.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffledRemaining[i], shuffledRemaining[j]] = [shuffledRemaining[j], shuffledRemaining[i]];
    }

    // Take a small percentage for discovery
    const discoverySegment = shuffledRemaining.slice(0, Math.ceil(shuffledRemaining.length * 0.2));

    // Now blend all segments together in an intelligent way
    finalBlendedQuestions = [
      ...featuredSegment,             // Start with top personalized content (1 question)
      ...personalizedSegment.slice(0, 2), // Then 2 personalized questions
      ...hotSegmentQuestions.slice(0, 2), // Then 2 questions from hot segments
      ...personalizedSegment.slice(2),    // Then rest of personalized
      ...hotSegmentQuestions.slice(2),    // Then rest of hot content
      ...discoverySegment              // Finally discovery content
    ];

    // 16. Update recently seen categories for future reference
    try {
      if (finalBlendedQuestions.length > 0) {
        const recentlySeenKey = `recentlySeenCategories_${userId}`;
        const recentlySeen = JSON.parse(localStorage.getItem(recentlySeenKey) || '{}');

        // Update with the top categories from this batch
        finalBlendedQuestions.slice(0, 3).forEach(q => {
          if (q.category) {
            recentlySeen[q.category] = Date.now();
          }
        });

        localStorage.setItem(recentlySeenKey, JSON.stringify(recentlySeen));
      }
    } catch (err) {
      console.warn('Error updating recently seen categories:', err);
    }

    // Log personalization stats for the top questions
    console.log(`Top 3 questions in blended feed:`,
      finalBlendedQuestions.slice(0, 3).map(q => ({
        id: q.id.substring(0, 6),
        category: q.category,
        score: q._personalizationScore ? q._personalizationScore.toFixed(2) : 'N/A',
        factors: q._scoreFactors ? Object.entries(q._scoreFactors).reduce((obj, [k, v]) => {
          obj[k] = v.toFixed(2);
          return obj;
        }, {}) : 'N/A'
      }))
    );

    console.log(`Returning ${finalBlendedQuestions.length} blended personalized questions`);

    // Remove the internal scoring data before returning
    return finalBlendedQuestions.map(q => {
      // eslint-disable-next-line no-unused-vars
      const { _personalizationScore, _scoreFactors, _metrics, ...question } = q;
      return question;
    });
  } catch (error) {
    console.error('Error in enhanced personalization algorithm:', error);
    // Return original questions if personalization fails
    return questions;
  }
};

// Extract categories from voting history
const getVotingHistoryCategories = (votedQuestions, allQuestions) => {
  const votingHistory = {};

  // Map question IDs to their categories
  const questionCategories = {};
  allQuestions.forEach(q => {
    if (q.id && q.category) {
      questionCategories[q.id] = q.category;
    }
  });

  // Count votes per category
  Object.entries(votedQuestions).forEach(([questionId, voteData]) => {
    // Handle both old and new vote data formats
    let category;

    if (typeof voteData === 'object' && voteData !== null) {
      // New format with metadata
      category = voteData.categoryId || questionCategories[questionId];
    } else {
      // Old format (just the option value)
      category = questionCategories[questionId];
    }

    if (category) {
      votingHistory[category] = (votingHistory[category] || 0) + 1;
    }
  });

  return votingHistory;
};

// Get the latest question (keeping for backward compatibility)
export const getLatestQuestion = async () => {
  try {
    console.log('Looking for latest question...');
    
    const result = await getAllQuestions(1, 1);
    console.log('Latest question result:', result);
    
    if (result.questions && result.questions.length > 0) {
      return result.questions[0];
    }
    
    // Special case: directly try the first_question document as fallback
    const firstQuestionRef = doc(db, 'questions', 'first_question');
    const firstQuestionSnap = await getDoc(firstQuestionRef);
    
    if (firstQuestionSnap.exists()) {
      console.log('Found first question with fixed ID as fallback');
      return {
        id: firstQuestionSnap.id,
        ...firstQuestionSnap.data()
      };
    }
    
    console.log('No questions found at all');
    return null;
  } catch (error) {
    console.error('Error getting latest question:', error);
    throw error;
  }
};

// Get a specific question by ID
export const getQuestionById = async (questionId) => {
  const docRef = doc(db, 'questions', questionId);
  const docSnap = await getDoc(docRef);
  
  if (!docSnap.exists()) {
    return null;
  }
  
  return {
    id: docSnap.id,
    ...docSnap.data()
  };
};

// Upload a new question with image or video
export const createQuestion = async (questionData, imageFile, thumbnailFile = null) => {
  try {
    // Determine if it's an image or video
    const isVideo = imageFile.type.startsWith('video/');
    const mediaType = isVideo ? 'video' : 'image';

    // First upload the media to Firebase Storage
    console.log(`Starting ${mediaType} upload to Firebase Storage`);
    const storageRef = ref(storage, `question_images/${Date.now()}_${imageFile.name}`);

    const uploadResult = await uploadBytes(storageRef, imageFile);
    console.log(`${mediaType} uploaded successfully:`, uploadResult);

    const imageUrl = await getDownloadURL(uploadResult.ref);
    console.log(`${mediaType} URL obtained:`, imageUrl);

    // Upload thumbnail if provided (for videos)
    let thumbnailUrl = null;
    if (thumbnailFile && isVideo) {
      console.log('Uploading thumbnail for video');
      const thumbnailRef = ref(storage, `question_images/thumbnails/${Date.now()}_${thumbnailFile.name}`);
      const thumbnailUploadResult = await uploadBytes(thumbnailRef, thumbnailFile);
      thumbnailUrl = await getDownloadURL(thumbnailUploadResult.ref);
      console.log('Thumbnail URL obtained:', thumbnailUrl);
    }

    // Then create the question document with the media URL
    const newQuestion = {
      ...questionData,
      imageUrl,
      mediaType, // Add media type field
      thumbnailUrl, // Add thumbnail URL if available
      optionAVotes: 0,
      optionBVotes: 0,
      totalVotes: 0,
      // Force a JavaScript Date object for createdAt instead of server timestamp
      // This ensures it's immediately available client-side
      createdAt: new Date().toISOString(),
      // Preserve the question type
      questionType: questionData.questionType || 'standard',
      // Default styling options if not provided
      styles: {
        optionABackgroundColor: questionData.styles?.optionABackgroundColor || 'white',
        optionATextColor: questionData.styles?.optionATextColor || 'black',
        optionBBackgroundColor: questionData.styles?.optionBBackgroundColor || 'black',
        optionBTextColor: questionData.styles?.optionBTextColor || 'white',
        resultBarColor: questionData.styles?.resultBarColor || 'white'
      }
    };

    // Include leaderboard options if this is a leaderboard question
    if (questionData.questionType === 'leaderboard') {
      newQuestion.leaderboardOptions = {
        showTomatoCount: questionData.leaderboardOptions?.showTomatoCount !== false,
        showClapCount: questionData.leaderboardOptions?.showClapCount === true,
        limit: questionData.leaderboardOptions?.limit || 3,
        sortBy: questionData.leaderboardOptions?.sortBy || 'tomatoes',
        // Store whitelist for allowed answers
        whitelist: questionData.leaderboardOptions?.whitelist || '',
        // Store banned words list
        bannedWords: questionData.leaderboardOptions?.bannedWords || ''
      };

      newQuestion.leaderboardStyles = {
        backgroundColor: questionData.leaderboardStyles?.backgroundColor || 'white',
        textColor: questionData.leaderboardStyles?.textColor || 'black',
        borderColor: questionData.leaderboardStyles?.borderColor || '#e0e0e0',
        borderRadius: questionData.leaderboardStyles?.borderRadius || '50px',
        boxShadow: questionData.leaderboardStyles?.boxShadow || '0 4px 15px rgba(0,0,0,0.1)',
        padding: questionData.leaderboardStyles?.padding || '12px 24px',
        fontSize: questionData.leaderboardStyles?.fontSize || '16px',
        fontWeight: questionData.leaderboardStyles?.fontWeight || '500',
        hoverBackgroundColor: questionData.leaderboardStyles?.hoverBackgroundColor || '#f5f5f5',
        activeBackgroundColor: questionData.leaderboardStyles?.activeBackgroundColor || '#e0e0e0'
      };
    }
    
    console.log('Creating new question document with unique ID');
    
    // For backward compatibility, use a fixed ID for the first question if none exist
    const existingQuestions = await getDocs(query(questionsRef, limit(1)));
    
    let questionRef;
    let questionId;
    
    if (existingQuestions.empty) {
      // No questions exist, use fixed ID for the first one
      questionId = 'first_question';
      questionRef = doc(db, 'questions', questionId);
      await setDoc(questionRef, newQuestion);
      console.log('Created first question with fixed ID: first_question');
    } else {
      // Questions already exist, use auto-generated ID
      questionRef = await addDoc(questionsRef, newQuestion);
      questionId = questionRef.id;
      console.log('Question document created successfully with ID:', questionId);
    }
    
    return {
      id: questionId,
      ...newQuestion
    };
  } catch (error) {
    console.error('Error in createQuestion:', error);
    throw error;
  }
};

// Submit a vote for a question
export const submitVote = async (questionId, option) => {
  const questionRef = doc(db, 'questions', questionId);
  const questionDoc = await getDoc(questionRef);
  
  if (!questionDoc.exists()) {
    throw new Error('Question does not exist');
  }
  
  const questionData = questionDoc.data();
  const updatedData = {
    totalVotes: questionData.totalVotes + 1
  };
  
  if (option === 'A') {
    updatedData.optionAVotes = questionData.optionAVotes + 1;
  } else if (option === 'B') {
    updatedData.optionBVotes = questionData.optionBVotes + 1;
  } else {
    throw new Error('Invalid vote option');
  }
  
  return updateDoc(questionRef, updatedData);
};

// Update an existing question
export const updateQuestion = async (questionId, questionData, imageFile = null) => {
  try {
    console.log('Updating question with ID:', questionId);
    const questionRef = doc(db, 'questions', questionId);

    // First check if the question exists
    const questionDoc = await getDoc(questionRef);
    if (!questionDoc.exists()) {
      throw new Error('Question does not exist');
    }

    // Prepare the update data
    const updateData = {
      ...questionData,
      // Preserve votes data
      optionAVotes: questionDoc.data().optionAVotes || 0,
      optionBVotes: questionDoc.data().optionBVotes || 0,
      totalVotes: questionDoc.data().totalVotes || 0,
      // Add updated timestamp
      updatedAt: new Date().toISOString()
    };

    // If there's a new media file, upload it
    if (imageFile) {
      const isVideo = imageFile.type.startsWith('video/');
      const mediaType = isVideo ? 'video' : 'image';
      console.log(`Uploading new ${mediaType} for question`);
      const storageRef = ref(storage, `question_images/${Date.now()}_${imageFile.name}`);
      const uploadResult = await uploadBytes(storageRef, imageFile);
      const imageUrl = await getDownloadURL(uploadResult.ref);

      // Add new media URL and type to update data
      updateData.imageUrl = imageUrl;
      updateData.mediaType = mediaType;
    } else {
      // Keep the existing media URL and type
      updateData.imageUrl = questionDoc.data().imageUrl;
      updateData.mediaType = questionDoc.data().mediaType || 'image'; // Default to image for backwards compatibility
    }

    // Update the question document
    await updateDoc(questionRef, updateData);
    console.log('Question successfully updated');

    return {
      id: questionId,
      ...updateData
    };
  } catch (error) {
    console.error('Error updating question:', error);
    throw error;
  }
};

// Delete a question
export const deleteQuestion = async (questionId) => {
  try {
    console.log('Deleting question with ID:', questionId);
    const questionRef = doc(db, 'questions', questionId);

    // First check if the question exists
    const questionDoc = await getDoc(questionRef);
    if (!questionDoc.exists()) {
      throw new Error('Question does not exist');
    }

    // Delete the question
    await deleteDoc(questionRef);
    console.log('Question successfully deleted');

    // Note: This doesn't delete the image from storage
    // In a production app, you'd want to also delete the associated image

    return true;
  } catch (error) {
    console.error('Error deleting question:', error);
    throw error;
  }
};

// Get comments for a question with pagination - HYBRID FIREBASE + LOCAL STORAGE
export const getCommentsForQuestion = async (questionId, page = 1, limit = 10) => {
  console.log(`[COMMENTS] Fetching comments for question ${questionId}, page ${page}, limit ${limit}`);
  
  if (!questionId) {
    console.error('[COMMENTS] Missing questionId in getCommentsForQuestion');
    return {
      comments: [],
      totalCount: 0,
      hasMore: false,
      page
    };
  }
  
  // Array to hold all comments (from both Firestore and localStorage)
  let allComments = [];
  
  // Step 1: Try to get comments from Firestore
  try {
    console.log('[COMMENTS] STEP 1: Getting comments from Firestore...');
    
    const firestoreQuery = query(
      collection(db, 'comments'),
      where('questionId', '==', questionId),
      where('parentId', '==', null)
    );
    
    const firestoreSnap = await getDocs(firestoreQuery);
    console.log(`[COMMENTS] Found ${firestoreSnap.size} comments in Firestore`);
    
    // Convert Firestore comments to a standard format
    firestoreSnap.forEach(doc => {
      const data = doc.data();
      allComments.push({
        id: doc.id,
        ...data,
        source: 'firebase',
        // Ensure localCreatedAt exists for consistent sorting
        localCreatedAt: data.localCreatedAt || 
          (data.createdAt && data.createdAt.toDate ? 
            data.createdAt.toDate().toISOString() : 
            new Date().toISOString())
      });
    });
  } catch (firestoreError) {
    console.warn('[COMMENTS] Error fetching from Firestore, continuing with local only:', firestoreError);
  }
  
  // Step 2: Get comments from localStorage (regardless of whether Firestore succeeded)
  console.log('[COMMENTS] STEP 2: Getting comments from localStorage...');
  
  try {
    const localComments = getLocalComments(questionId);
    console.log(`[COMMENTS] Found ${localComments.length} comments in localStorage`);
    
    // Add the local comments to the array with a source flag
    localComments.forEach(comment => {
      // Only add if not already present (avoid duplicates)
      if (!allComments.some(c => c.id === comment.id)) {
        allComments.push({
          ...comment,
          source: 'local'
        });
      }
    });
  } catch (localError) {
    console.warn('[COMMENTS] Error fetching from localStorage:', localError);
  }
  
  // Step 3: Combine and sort all comments
  console.log(`[COMMENTS] STEP 3: Processing ${allComments.length} total comments...`);
  
  if (allComments.length === 0) {
    console.log('[COMMENTS] No comments found from any source');
    return {
      comments: [],
      totalCount: 0,
      hasMore: false,
      page
    };
  }
  
  // Sort by timestamp (newest first)
  allComments.sort((a, b) => {
    const aTimestamp = a.localCreatedAt ? new Date(a.localCreatedAt).getTime() : 0;
    const bTimestamp = b.localCreatedAt ? new Date(b.localCreatedAt).getTime() : 0;
    return bTimestamp - aTimestamp; // Newest first
  });
  
  // Apply pagination
  const startIndex = (page - 1) * limit;
  const endIndex = startIndex + limit;
  const paginatedComments = allComments.slice(startIndex, endIndex);
  
  console.log(`[COMMENTS] Returning ${paginatedComments.length} comments`);
  
  // Return the combined result
  return {
    comments: paginatedComments,
    totalCount: allComments.length,
    hasMore: allComments.length > endIndex,
    page,
    sources: {
      firebase: allComments.filter(c => c.source === 'firebase').length,
      local: allComments.filter(c => c.source === 'local').length
    }
  };
};

// Get replies for a comment - HYBRID FIREBASE + LOCAL STORAGE
export const getRepliesForComment = async (commentId) => {
  console.log(`[REPLIES] Fetching replies for comment ${commentId}`);
  
  if (!commentId) {
    console.error('[REPLIES] Missing commentId in getRepliesForComment');
    return [];
  }
  
  // Array to hold all replies (from both Firestore and localStorage)
  let allReplies = [];
  
  // Step 1: Try to get replies from Firestore
  try {
    console.log('[REPLIES] STEP 1: Getting replies from Firestore...');
    
    const firestoreQuery = query(
      collection(db, 'comments'),
      where('parentId', '==', commentId)
    );
    
    const firestoreSnap = await getDocs(firestoreQuery);
    console.log(`[REPLIES] Found ${firestoreSnap.size} replies in Firestore`);
    
    // Convert Firestore replies to a standard format
    firestoreSnap.forEach(doc => {
      const data = doc.data();
      allReplies.push({
        id: doc.id,
        ...data,
        source: 'firebase',
        // Ensure localCreatedAt exists for consistent sorting
        localCreatedAt: data.localCreatedAt || 
          (data.createdAt && data.createdAt.toDate ? 
            data.createdAt.toDate().toISOString() : 
            new Date().toISOString())
      });
    });
  } catch (firestoreError) {
    console.warn('[REPLIES] Error fetching from Firestore, continuing with local only:', firestoreError);
  }
  
  // Step 2: Get replies from localStorage (regardless of whether Firestore succeeded)
  console.log('[REPLIES] STEP 2: Getting replies from localStorage...');
  
  try {
    const localReplies = getLocalReplies(commentId);
    console.log(`[REPLIES] Found ${localReplies.length} replies in localStorage`);
    
    // Add the local replies to the array with a source flag
    localReplies.forEach(reply => {
      // Only add if not already present (avoid duplicates)
      if (!allReplies.some(r => r.id === reply.id)) {
        allReplies.push({
          ...reply,
          source: 'local'
        });
      }
    });
  } catch (localError) {
    console.warn('[REPLIES] Error fetching from localStorage:', localError);
  }
  
  // Step 3: Sort all replies by timestamp
  console.log(`[REPLIES] STEP 3: Processing ${allReplies.length} total replies...`);
  
  if (allReplies.length === 0) {
    console.log('[REPLIES] No replies found from any source');
    return [];
  }
  
  // Sort by timestamp (oldest first for replies)
  allReplies.sort((a, b) => {
    const aTimestamp = a.localCreatedAt ? new Date(a.localCreatedAt).getTime() : 0;
    const bTimestamp = b.localCreatedAt ? new Date(b.localCreatedAt).getTime() : 0;
    return aTimestamp - bTimestamp; // Oldest first
  });
  
  console.log(`[REPLIES] Returning ${allReplies.length} replies`);
  
  // Return all replies (no pagination for replies)
  return allReplies.map(reply => ({
    ...reply,
    // Ensure all required fields are present
    questionId: reply.questionId || '',
    userId: reply.userId || 'unknown_user',
    userName: reply.userName || 'Unknown User',
    text: reply.text || '(No content)',
    parentId: reply.parentId || commentId,
    // For compatibility with Firestore timestamps in the UI
    createdAt: {
      toDate: () => new Date(reply.localCreatedAt || new Date())
    }
  }));
};

// Simple function to sanitize text (prevents basic HTML/script injection)
const sanitizeText = (text) => {
  if (!text) return '';
  return text
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .trim();
};

// Export function to clear local comments
export const clearAllComments = () => {
  return clearAllLocalComments();
};

// Delete a comment - HYBRID FIREBASE + LOCAL STORAGE
export const deleteComment = async (commentId, questionId, userId, isAdmin = false) => {
  console.log('Starting deleteComment with params:', { commentId, questionId, userId, isAdmin });
  
  if (!commentId) {
    console.error('Missing commentId in deleteComment');
    throw new Error('Comment ID is required');
  }
  
  if (!userId) {
    console.error('Missing userId in deleteComment');
    throw new Error('User ID is required');
  }
  
  // Track if we were able to delete from Firebase
  let firebaseDeleted = false;
  
  // Step 1: Try to delete from Firebase first
  try {
    console.log('[DELETE] Attempting to delete comment from Firebase');
    
    // First check if the comment exists and if user has permission to delete it
    const commentRef = doc(db, 'comments', commentId);
    const commentSnap = await getDoc(commentRef);
    
    if (commentSnap.exists()) {
      const commentData = commentSnap.data();
      
      // Double-check permissions for security
      if (isAdmin || commentData.userId === userId) {
        // Delete from Firestore
        await deleteDoc(commentRef);
        console.log('[DELETE] Successfully deleted comment from Firebase');
        firebaseDeleted = true;
      } else {
        console.error('[DELETE] User does not have permission to delete this comment');
        console.error('[DELETE] Comment belongs to userId:', commentData.userId, 'requesting user:', userId);
        throw new Error('You do not have permission to delete this comment');
      }
    } else {
      console.warn('[DELETE] Comment not found in Firebase, might be local-only');
    }
  } catch (firebaseError) {
    console.error('[DELETE] Error deleting from Firebase:', firebaseError);
    
    // If it's a permission error, throw it
    if (firebaseError.message.includes('permission')) {
      throw firebaseError;
    }
    // Otherwise, continue to try local storage
  }
  
  // Step 2: Delete from local storage as well (regardless of whether Firebase succeeded)
  try {
    console.log('[DELETE] Attempting to delete comment from localStorage');
    const localDeleted = deleteLocalComment(commentId, questionId, userId, isAdmin);
    console.log('[DELETE] Local storage delete result:', localDeleted);
    
    // If neither Firebase nor localStorage succeeded, throw an error
    if (!firebaseDeleted && !localDeleted) {
      console.error('[DELETE] Failed to delete comment from both Firebase and localStorage');
      throw new Error('Failed to delete comment. Please try again later.');
    }
    
    return { success: true, firebaseDeleted, localDeleted };
  } catch (localError) {
    console.error('[DELETE] Error deleting from localStorage:', localError);
    
    // If we already deleted from Firebase, we still succeeded partially
    if (firebaseDeleted) {
      return { success: true, firebaseDeleted, localDeleted: false };
    }
    
    // If we get here, both delete operations failed
    throw new Error('Failed to delete comment. Please try again later.');
  }
};

// Delete a reply - HYBRID FIREBASE + LOCAL STORAGE
export const deleteReply = async (replyId, parentId, userId, isAdmin = false) => {
  console.log('Starting deleteReply with params:', { replyId, parentId, userId, isAdmin });
  
  if (!replyId || !parentId) {
    console.error('Missing required params in deleteReply');
    throw new Error('Reply ID and parent comment ID are required');
  }
  
  if (!userId) {
    console.error('Missing userId in deleteReply');
    throw new Error('User ID is required');
  }
  
  // Track if we were able to delete from Firebase
  let firebaseDeleted = false;
  
  // Step 1: Try to delete from Firebase first
  try {
    console.log('[DELETE] Attempting to delete reply from Firebase');
    
    // First check if the reply exists and if user has permission to delete it
    const replyRef = doc(db, 'comments', replyId);
    const replySnap = await getDoc(replyRef);
    
    if (replySnap.exists()) {
      const replyData = replySnap.data();
      
      // Double-check permissions for security - admin or owner only
      if (isAdmin || replyData.userId === userId) {
        // Delete from Firestore
        await deleteDoc(replyRef);
        console.log('[DELETE] Successfully deleted reply from Firebase');
        firebaseDeleted = true;
      } else {
        console.error('[DELETE] User does not have permission to delete this reply');
        console.error('[DELETE] Reply belongs to userId:', replyData.userId, 'requesting user:', userId);
        throw new Error('You do not have permission to delete this reply');
      }
    } else {
      console.warn('[DELETE] Reply not found in Firebase, might be local-only');
    }
  } catch (firebaseError) {
    console.error('[DELETE] Error deleting from Firebase:', firebaseError);
    
    // If it's a permission error, throw it immediately
    if (firebaseError.message.includes('permission')) {
      throw firebaseError;
    }
    // Otherwise, continue to try local storage
  }
  
  // Step 2: Delete from local storage as well (regardless of whether Firebase succeeded)
  try {
    console.log('[DELETE] Attempting to delete reply from localStorage');
    const localDeleted = deleteLocalReply(replyId, parentId, userId, isAdmin);
    console.log('[DELETE] Local storage delete result:', localDeleted);
    
    // If neither Firebase nor localStorage succeeded, throw an error
    if (!firebaseDeleted && !localDeleted) {
      console.error('[DELETE] Failed to delete reply from both Firebase and localStorage');
      throw new Error('Failed to delete reply. Please try again later.');
    }
    
    return { success: true, firebaseDeleted, localDeleted };
  } catch (localError) {
    console.error('[DELETE] Error deleting from localStorage:', localError);
    
    // If we already deleted from Firebase, we still succeeded partially
    if (firebaseDeleted) {
      return { success: true, firebaseDeleted, localDeleted: false };
    }
    
    // If we get here, both delete operations failed
    throw new Error('Failed to delete reply. Please try again later.');
  }
};

// Check if a user is an admin
export const isUserAdmin = (userId) => {
  if (!userId) return false;
  
  // Log the user ID being checked
  console.log('Checking if user is admin:', userId);
  
  // PRODUCTION VERSION - use a list of admin user IDs
  const adminUserIds = [
    'admin123', 
    'ownerUser'
  ];
  return adminUserIds.includes(userId);
};

// Function to check if a user is blocked before allowing them to comment
export const checkUserCanComment = async (userId) => {
  if (!userId) return { canComment: false, reason: 'No user ID provided' };
  
  try {
    // Import the isUserBlocked function to avoid circular dependencies
    const { isUserBlocked } = require('./userService');
    
    // Check if user is blocked
    const isBlocked = await isUserBlocked(userId);
    
    if (isBlocked) {
      return { 
        canComment: false, 
        reason: 'User is blocked from commenting'
      };
    }
    
    return { canComment: true };
  } catch (error) {
    console.error('Error checking if user can comment:', error);
    // Default to allowing comments if there's an error checking
    return { 
      canComment: true, 
      error: error.message 
    };
  }
};

// Upload a user profile photo
export const uploadUserProfilePhoto = async (userId, imageFile) => {
  if (!userId || !imageFile) return null;
  
  try {
    // Create a reference to the user's profile photo
    const storageRef = ref(storage, `user_profiles/${userId}_${Date.now()}`);
    
    // Upload the file
    const uploadResult = await uploadBytes(storageRef, imageFile);
    console.log('Profile photo uploaded successfully:', uploadResult);
    
    // Get the download URL
    const downloadURL = await getDownloadURL(uploadResult.ref);
    console.log('Profile photo URL:', downloadURL);
    
    // Update the user's profile in firestore
    const userRef = doc(db, 'users', userId);
    await setDoc(userRef, { 
      photoURL: downloadURL,
      updatedAt: serverTimestamp()
    }, { merge: true });
    
    // Also update local storage
    const localUserProfiles = JSON.parse(localStorage.getItem('userProfiles') || '{}');
    localUserProfiles[userId] = {
      ...localUserProfiles[userId],
      photoURL: downloadURL,
      updatedAt: new Date().toISOString()
    };
    localStorage.setItem('userProfiles', JSON.stringify(localUserProfiles));
    
    return downloadURL;
  } catch (error) {
    console.error('Error uploading profile photo:', error);
    throw error;
  }
};

// Get user profile data
export const getUserProfile = async (userId) => {
  if (!userId) return null;
  
  try {
    // First check local storage
    const localUserProfiles = JSON.parse(localStorage.getItem('userProfiles') || '{}');
    
    // If it's in local storage and recent (within 24 hours), use that
    if (localUserProfiles[userId]) {
      const updatedAt = new Date(localUserProfiles[userId].updatedAt || 0);
      const now = new Date();
      const hoursSinceUpdate = (now - updatedAt) / (1000 * 60 * 60);
      
      if (hoursSinceUpdate < 24) {
        return localUserProfiles[userId];
      }
    }
    
    // Otherwise, fetch from Firestore
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      const userData = userSnap.data();
      
      // Update local storage
      localUserProfiles[userId] = {
        ...userData,
        updatedAt: new Date().toISOString()
      };
      localStorage.setItem('userProfiles', JSON.stringify(localUserProfiles));
      
      return userData;
    }
    
    // If no profile exists yet, return default values
    return { photoURL: null };
  } catch (error) {
    console.error('Error getting user profile:', error);
    // Return any locally cached version on error
    const localUserProfiles = JSON.parse(localStorage.getItem('userProfiles') || '{}');
    return localUserProfiles[userId] || { photoURL: null };
  }
};

// Direct test function to verify Firestore connectivity for comments
export const testDirectCommentWrite = async (questionId = 'test-question') => {
  try {
    console.log('DIRECT TEST: Beginning direct Firestore write test');
    
    // Create a simple test comment
    const testComment = {
      questionId: questionId,
      userId: 'test-user-' + Date.now(),
      userName: 'Test User',
      text: 'This is a test comment created at ' + new Date().toISOString(),
      parentId: null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      localCreatedAt: new Date().toISOString(),
      isTest: true
    };
    
    console.log('DIRECT TEST: Writing test comment to Firestore:', testComment);
    
    // Write directly to Firestore
    const docRef = await addDoc(commentsRef, testComment);
    console.log('DIRECT TEST: Successfully wrote test comment with ID:', docRef.id);
    
    // Read it back
    const docSnap = await getDoc(doc(db, 'comments', docRef.id));
    
    if (docSnap.exists()) {
      console.log('DIRECT TEST: Successfully read back test comment:', docSnap.data());
      return {
        success: true,
        id: docRef.id,
        data: docSnap.data()
      };
    } else {
      console.error('DIRECT TEST: Failed to read back test comment!');
      return {
        success: false,
        error: 'Document not found after writing'
      };
    }
  } catch (error) {
    console.error('DIRECT TEST: Error during direct Firestore test:', error);
    return {
      success: false,
      error: error.message || 'Unknown error',
      errorCode: error.code
    };
  }
};


// Add a comment to a question - HYBRID FIREBASE + LOCAL STORAGE
export const addComment = async (questionId, userId, userName, text, parentId = null, userVoteOption = null) => {
  console.log('Starting addComment with params:', { 
    questionId, 
    userId, 
    userName, 
    text, 
    parentId, 
    userVoteOption 
  });
  
  // Validate inputs
  if (!questionId) {
    console.error('Missing questionId in addComment');
    throw new Error('Question ID is required');
  }
  
  if (!text || text.trim() === '') {
    console.error('Missing text in addComment');
    throw new Error('Comment text is required');
  }
  
  // Check if user is blocked from commenting
  try {
    const { isUserBlocked } = require('./userService');
    const isBlocked = await isUserBlocked(userId);
    
    if (isBlocked) {
      console.error('Blocked user attempted to comment:', userId);
      throw new Error('You are not allowed to comment at this time.');
    }
  } catch (error) {
    if (error.message === 'You are not allowed to comment at this time.') {
      throw error; // Re-throw the blocked error
    }
    // If there's an error checking blocked status, log but continue
    console.warn('Error checking if user is blocked:', error);
  }
  
  // Apply input validation
  const sanitizedText = sanitizeText(text);
  
  // Check comment length - don't allow extremely long comments
  if (sanitizedText.length > 1000) {
    console.error('Comment text too long');
    throw new Error('Comment is too long (maximum 1000 characters)');
  }
  
  // Try to get user profile for photo
  let userPhotoURL = null;
  try {
    const userProfile = await getUserProfile(userId);
    if (userProfile && userProfile.photoURL) {
      userPhotoURL = userProfile.photoURL;
    }
  } catch (err) {
    console.warn('Could not get user profile photo:', err);
  }
  
  // Construct the comment object
  const newComment = {
    questionId,
    userId: userId || 'anonymous_user',
    userName: sanitizeText(userName) || 'User',
    text: sanitizedText,
    parentId: parentId || null,
    // Use timestamp for display
    localCreatedAt: new Date().toISOString(),
    userPhotoURL: userPhotoURL,
    // Store the vote option with the comment
    option: userVoteOption
  };
  
  // For replies, add parent comment id
  if (parentId) {
    newComment.parentId = parentId;
  }
  
  try {
    // FIREBASE ATTEMPT - TRY FIRST
    console.log('Attempting to save comment to Firebase...');
    
    // Add server timestamp for Firebase sorting
    const firebaseComment = {
      ...newComment,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    // Try to write to Firestore
    const docRef = await addDoc(commentsRef, firebaseComment);
    console.log('Comment successfully saved to Firebase with ID:', docRef.id);
    
    // Return the Firebase comment
    return {
      ...newComment,
      id: docRef.id,
      firebaseSaved: true,
      // For immediate display in the UI
      createdAt: {
        toDate: () => new Date()
      }
    };
  } catch (firebaseError) {
    console.error('Firebase save failed, using local storage fallback:', firebaseError);
    
    // FALLBACK TO LOCAL STORAGE
    console.log('Saving comment to local storage instead...');
    
    if (parentId) {
      // This is a reply
      const localReply = saveReplyLocally({
        ...newComment,
        createdAt: new Date().toISOString()
      });
      
      if (localReply) {
        console.log('Reply saved to local storage:', localReply);
        return {
          ...localReply,
          // For compatibility with Firebase objects
          createdAt: {
            toDate: () => new Date(localReply.localCreatedAt)
          }
        };
      }
    } else {
      // This is a regular comment
      const localComment = saveCommentLocally({
        ...newComment,
        createdAt: new Date().toISOString()
      });
      
      if (localComment) {
        console.log('Comment saved to local storage:', localComment);
        return {
          ...localComment,
          // For compatibility with Firebase objects
          createdAt: {
            toDate: () => new Date(localComment.localCreatedAt)
          }
        };
      }
    }
    
    // If both Firebase and local storage fail
    console.error('Both Firebase and local storage failed to save comment');
    throw new Error('Unable to save your comment. Please try again later.');
  }
};

// Clap for a comment - HYBRID FIREBASE + LOCAL STORAGE
export const clapComment = async (commentId, userId) => {
  console.log('Starting clapComment with params:', { commentId, userId });

  if (!commentId) {
    console.error('Missing commentId in clapComment');
    throw new Error('Comment ID is required');
  }

  if (!userId) {
    console.error('Missing userId in clapComment');
    throw new Error('User ID is required');
  }

  try {
    // Try to update in Firebase first
    console.log('Attempting to clap for comment in Firebase...');

    // Get the comment from Firestore
    const commentRef = doc(db, 'comments', commentId);
    const commentSnap = await getDoc(commentRef);

    if (commentSnap.exists()) {
      const commentData = commentSnap.data();

      // Get the current clap count or initialize to 0
      const currentCount = commentData.clapCount || 0;

      // Get the users who clapped or initialize to empty array
      const clapUsers = commentData.clapUsers || [];

      // Check if user has already clapped
      if (clapUsers.includes(userId)) {
        console.log('User has already clapped for this comment');
        return {
          success: false,
          message: 'You have already clapped for this comment'
        };
      }

      // Update the clap count and add user to the list
      await updateDoc(commentRef, {
        clapCount: currentCount + 1,
        clapUsers: [...clapUsers, userId]
      });

      console.log('Clap added successfully in Firebase');
      return {
        success: true,
        clapCount: currentCount + 1,
        firebaseSaved: true
      };
    }

    // If comment is not in Firebase, try to update in local storage
    console.log('Comment not found in Firebase, trying local storage...');

    // Get all local comments for the question
    const localComments = JSON.parse(localStorage.getItem('comments') || '{}');

    let locallyUpdated = false;

    // Iterate through all question's comments
    Object.keys(localComments).forEach(questionId => {
      const comments = localComments[questionId];

      // Find the comment
      const commentIndex = comments.findIndex(c => c.id === commentId);

      if (commentIndex !== -1) {
        const comment = comments[commentIndex];

        // Get the current clap count or initialize to 0
        const currentCount = comment.clapCount || 0;

        // Get the users who clapped or initialize to empty array
        const clapUsers = comment.clapUsers || [];

        // Check if user has already clapped
        if (clapUsers.includes(userId)) {
          console.log('User has already clapped for this comment in local storage');
          return {
            success: false,
            message: 'You have already clapped for this comment'
          };
        }

        // Update the clap count and add user to the list
        comment.clapCount = currentCount + 1;
        comment.clapUsers = [...clapUsers, userId];

        // Update the comment in local storage
        comments[commentIndex] = comment;
        localStorage.setItem('comments', JSON.stringify(localComments));

        console.log('Clap added successfully in local storage');
        locallyUpdated = true;
        return {
          success: true,
          clapCount: currentCount + 1,
          firebaseSaved: false
        };
      }
    });

    if (!locallyUpdated) {
      console.error('Comment not found in local storage either');
      throw new Error('Comment not found');
    }

    // Return default success if we get here
    return {
      success: true
    };
  } catch (error) {
    console.error('Error clapping for comment:', error);
    throw error;
  }
};

// Throw a tomato at a comment - HYBRID FIREBASE + LOCAL STORAGE
export const throwTomato = async (commentId, userId) => {
  console.log('Starting throwTomato with params:', { commentId, userId });

  if (!commentId) {
    console.error('Missing commentId in throwTomato');
    throw new Error('Comment ID is required');
  }

  if (!userId) {
    console.error('Missing userId in throwTomato');
    throw new Error('User ID is required');
  }

  try {
    // Try to update in Firebase first
    console.log('Attempting to throw tomato in Firebase...');

    // Get the comment from Firestore
    const commentRef = doc(db, 'comments', commentId);
    const commentSnap = await getDoc(commentRef);

    if (commentSnap.exists()) {
      const commentData = commentSnap.data();

      // Get the current tomato count or initialize to 0
      const currentCount = commentData.tomatoCount || 0;

      // Get the users who threw tomatoes or initialize to empty array
      const tomatoUsers = commentData.tomatoUsers || [];

      // Check if user has already thrown a tomato
      if (tomatoUsers.includes(userId)) {
        console.log('User has already thrown a tomato at this comment');
        return {
          success: false,
          message: 'You have already thrown a tomato at this comment'
        };
      }

      // Get the splats from the comment if they exist
      let updatedSplats = commentData.splats || [];
      
      // Try to get splats from the event that might have been triggered
      const splatsEvent = localStorage.getItem(`splats_${commentId}`);
      if (splatsEvent) {
        try {
          const parsedSplats = JSON.parse(splatsEvent);
          if (Array.isArray(parsedSplats) && parsedSplats.length > 0) {
            updatedSplats = parsedSplats;
          }
          // Clear the temporary storage
          localStorage.removeItem(`splats_${commentId}`);
        } catch (e) {
          console.error("Error parsing splats from localStorage:", e);
        }
      }
      
      // Update the tomato count, user list, and splats
      await updateDoc(commentRef, {
        tomatoCount: currentCount + 1,
        tomatoUsers: [...tomatoUsers, userId],
        splats: updatedSplats
      });

      console.log('Tomato thrown successfully in Firebase');
      return {
        success: true,
        tomatoCount: currentCount + 1,
        firebaseSaved: true
      };
    }

    // If comment is not in Firebase, try to update in local storage
    console.log('Comment not found in Firebase, trying local storage...');

    // Get all local comments for the question
    const localComments = JSON.parse(localStorage.getItem('comments') || '{}');

    let locallyUpdated = false;

    // Iterate through all question's comments
    Object.keys(localComments).forEach(questionId => {
      const comments = localComments[questionId];

      // Find the comment
      const commentIndex = comments.findIndex(c => c.id === commentId);

      if (commentIndex !== -1) {
        const comment = comments[commentIndex];

        // Get the current tomato count or initialize to 0
        const currentCount = comment.tomatoCount || 0;

        // Get the users who threw tomatoes or initialize to empty array
        const tomatoUsers = comment.tomatoUsers || [];

        // Check if user has already thrown a tomato
        if (tomatoUsers.includes(userId)) {
          console.log('User has already thrown a tomato at this comment in local storage');
          return {
            success: false,
            message: 'You have already thrown a tomato at this comment'
          };
        }

        // Update the tomato count and add user to the list
        comment.tomatoCount = currentCount + 1;
        comment.tomatoUsers = [...tomatoUsers, userId];

        // Update the comment in local storage
        comments[commentIndex] = comment;
        localStorage.setItem('comments', JSON.stringify(localComments));

        console.log('Tomato thrown successfully in local storage');
        locallyUpdated = true;
        return {
          success: true,
          tomatoCount: currentCount + 1,
          firebaseSaved: false
        };
      }
    });

    if (!locallyUpdated) {
      console.error('Comment not found in local storage either');
      throw new Error('Comment not found');
    }

    // Return default success if we get here
    return {
      success: true
    };
  } catch (error) {
    console.error('Error throwing tomato:', error);
    throw error;
  }
};

// Remove a tomato from a comment (untomato) - HYBRID FIREBASE + LOCAL STORAGE
export const removeTomato = async (commentId, userId) => {
  console.log('Starting removeTomato with params:', { commentId, userId });

  if (!commentId) {
    console.error('Missing commentId in removeTomato');
    throw new Error('Comment ID is required');
  }

  if (!userId) {
    console.error('Missing userId in removeTomato');
    throw new Error('User ID is required');
  }

  try {
    // Try to update in Firebase first
    console.log('Attempting to remove tomato in Firebase...');

    // Get the comment from Firestore
    const commentRef = doc(db, 'comments', commentId);
    const commentSnap = await getDoc(commentRef);

    if (commentSnap.exists()) {
      const commentData = commentSnap.data();

      // Get the current tomato count or initialize to 0
      const currentCount = commentData.tomatoCount || 0;

      // Get the users who threw tomatoes or initialize to empty array
      const tomatoUsers = commentData.tomatoUsers || [];

      // Check if user has thrown a tomato
      if (!tomatoUsers.includes(userId)) {
        console.log('User has not thrown a tomato at this comment');
        return {
          success: false,
          message: 'You have not thrown a tomato at this comment'
        };
      }

      // Make sure count doesn't go below 0
      const newCount = Math.max(0, currentCount - 1);

      // Get the splats from the comment if they exist
      let updatedSplats = commentData.splats || [];
      
      // Try to get splats from the event that might have been triggered
      const splatsEvent = localStorage.getItem(`splats_${commentId}`);
      if (splatsEvent) {
        try {
          const parsedSplats = JSON.parse(splatsEvent);
          if (Array.isArray(parsedSplats)) {
            updatedSplats = parsedSplats;
          }
          // Clear the temporary storage
          localStorage.removeItem(`splats_${commentId}`);
        } catch (e) {
          console.error("Error parsing splats from localStorage:", e);
        }
      } else if (updatedSplats.length > 0) {
        // If no event but we have splats, remove the last one
        updatedSplats = updatedSplats.slice(0, -1);
      }
      
      // Update the tomato count, user list, and splats
      await updateDoc(commentRef, {
        tomatoCount: newCount,
        tomatoUsers: tomatoUsers.filter(uid => uid !== userId),
        splats: updatedSplats
      });

      console.log('Tomato removed successfully in Firebase');
      return {
        success: true,
        tomatoCount: newCount,
        firebaseSaved: true
      };
    }

    // If comment is not in Firebase, try to update in local storage
    console.log('Comment not found in Firebase, trying local storage...');

    // Get all local comments for the question
    const localComments = JSON.parse(localStorage.getItem('comments') || '{}');

    let locallyUpdated = false;

    // Iterate through all question's comments
    Object.keys(localComments).forEach(questionId => {
      const comments = localComments[questionId];

      // Find the comment
      const commentIndex = comments.findIndex(c => c.id === commentId);

      if (commentIndex !== -1) {
        const comment = comments[commentIndex];

        // Get the current tomato count or initialize to 0
        const currentCount = comment.tomatoCount || 0;

        // Get the users who threw tomatoes or initialize to empty array
        const tomatoUsers = comment.tomatoUsers || [];

        // Check if user has thrown a tomato
        if (!tomatoUsers.includes(userId)) {
          console.log('User has not thrown a tomato at this comment in local storage');
          return {
            success: false,
            message: 'You have not thrown a tomato at this comment'
          };
        }

        // Make sure count doesn't go below 0
        const newCount = Math.max(0, currentCount - 1);

        // Update the tomato count and remove user from the list
        comment.tomatoCount = newCount;
        comment.tomatoUsers = tomatoUsers.filter(uid => uid !== userId);

        // Update the comment in local storage
        comments[commentIndex] = comment;
        localStorage.setItem('comments', JSON.stringify(localComments));

        console.log('Tomato removed successfully in local storage');
        locallyUpdated = true;
        return {
          success: true,
          tomatoCount: newCount,
          firebaseSaved: false
        };
      }
    });

    if (!locallyUpdated) {
      console.error('Comment not found in local storage either');
      throw new Error('Comment not found');
    }

    // Return default success if we get here
    return {
      success: true
    };
  } catch (error) {
    console.error('Error removing tomato:', error);
    throw error;
  }
};

// Helper function to create sample questions if database is empty
const createSampleQuestionsIfEmpty = async () => {
  console.log('Creating sample questions to populate empty database...');
  
  const sampleQuestions = [
    {
      questionText: "What's the best pizza topping?",
      optionA: "Pepperoni",
      optionB: "Mushrooms",
      category: "food"
    },
    {
      questionText: "Which social media platform is better?",
      optionA: "Instagram", 
      optionB: "TikTok",
      category: "technology"
    },
    {
      questionText: "What's the ideal vacation?",
      optionA: "Beach resort",
      optionB: "Mountain hiking", 
      category: "lifestyle"
    },
    {
      questionText: "Who is the G.O.A.T. basketball player?",
      optionA: "Comment your answer",
      optionB: "See top answers",
      category: "sports",
      questionType: "leaderboard",
      leaderboardOptions: {
        showTomatoCount: true,
        showClapCount: false,
        limit: 3,
        sortBy: "tomatoes"
      }
    },
    {
      questionText: "Which programming language is best for beginners?",
      optionA: "Python",
      optionB: "JavaScript",
      category: "technology"
    }
  ];

  try {
    // Create a simple placeholder image
    const canvas = document.createElement('canvas');
    canvas.width = 400;
    canvas.height = 300;
    const ctx = canvas.getContext('2d');
    
    // Create a gradient background
    const gradient = ctx.createLinearGradient(0, 0, 400, 300);
    gradient.addColorStop(0, '#667eea');
    gradient.addColorStop(1, '#764ba2');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 400, 300);
    
    // Add some text
    ctx.fillStyle = 'white';
    ctx.font = '24px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('Sample Question', 200, 150);
    
    return new Promise((resolve) => {
      canvas.toBlob(async (blob) => {
        const imageFile = new File([blob], 'sample.png', { type: 'image/png' });
        
        for (const questionData of sampleQuestions) {
          try {
            console.log('Creating sample question:', questionData.questionText);
            await createQuestion(questionData, imageFile);
            // Small delay to avoid overwhelming Firebase
            await new Promise(r => setTimeout(r, 500));
          } catch (error) {
            console.error('Error creating sample question:', error);
          }
        }
        
        console.log('Finished creating sample questions');
        resolve();
      });
    });
  } catch (error) {
    console.error('Error in createSampleQuestionsIfEmpty:', error);
  }
};